import { useEffect, useRef, useState } from 'react';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const GREETING: ChatMessage = {
  role: 'assistant',
  content: "Hi! I'm the Proven Realty Group concierge. Ask me anything about buying, selling, pricing, or our AI tools — how can I help?",
};

interface ConciergeChatProps {
  variant?: 'panel' | 'page';
}

export default function ConciergeChat({ variant = 'panel' }: ConciergeChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([GREETING]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;

    const next = [...messages, { role: 'user' as const, content: text }];
    setMessages(next);
    setInput('');
    setLoading(true);
    setError(null);

    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/ai-concierge-chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${supabaseKey}` },
        body: JSON.stringify({ messages: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chat failed');
      setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`flex flex-col ${variant === 'page' ? 'h-[70vh]' : 'h-full'}`}>
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === 'user'
                ? 'bg-brand-500 text-white rounded-br-sm'
                : 'bg-warm-100 dark:bg-dark-700 text-dark-800 dark:text-dark-100 rounded-bl-sm'
            }`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="px-4 py-3 rounded-2xl bg-warm-100 dark:bg-dark-700">
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-dark-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-dark-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-dark-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        {error && <p className="text-red-500 text-xs text-center">{error}</p>}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="p-3 border-t border-warm-200 dark:border-dark-700 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about buying, selling, pricing…"
          className="flex-1 px-4 py-2.5 rounded-full border border-warm-200 dark:border-dark-600 text-sm bg-white dark:bg-dark-900 text-dark-900 dark:text-white focus:outline-none focus:border-brand-500"
        />
        <button type="submit" disabled={loading} className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-500 text-white hover:bg-brand-600 transition-colors disabled:opacity-60 flex-shrink-0">
          <i className="ri-send-plane-fill" />
        </button>
      </form>
    </div>
  );
}
